import type { VariantProps } from 'class-variance-authority'
import type { HTMLAttributes, ReactNode } from 'react'
import { forwardRef } from 'react'
import { cn } from '@/lib/utils'
import { cva } from 'class-variance-authority'

const cardVariants = cva(
  'transition-tonal duration-base ease-ios-in-out rounded-card bg-card text-card-foreground',
  {
    variants: {
      variant: {
        default: 'border-0 bg-card',
        primary:
          'border-primary/20 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent',
        success:
          'border-green-500/20 bg-gradient-to-br from-green-500/10 via-green-500/5 to-transparent',
        warning:
          'border-amber-500/20 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent',
        accent:
          'from-accent-blue/10 via-accent-purple/5 border-accent-blue/20 bg-gradient-to-br to-transparent',
        destructive:
          'border-destructive/20 bg-gradient-to-br from-destructive/10 via-destructive/5 to-transparent',
        glass:
          'border border-border/20 bg-background/40 hover:bg-background/50',
        gradient:
          'via-accent-purple/10 to-accent-blue/20 border-primary/30 bg-gradient-to-br from-primary/20',
        elevated: 'border border-border/30 bg-card shadow-sm hover:shadow-md',
        outline: 'border border-border/40 bg-card hover:border-border/60',
      },
      hover: {
        true: 'cursor-pointer hover:-translate-y-0.5 hover:shadow-sm',
        false: '',
      },
      interactive: {
        true: 'group cursor-pointer',
        false: '',
      },
      // Opt-in iOS press feedback for cards that are themselves clickable.
      pressable: {
        true: 'active:duration-instant cursor-pointer select-none active:scale-[0.985]',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      hover: false,
      interactive: false,
      pressable: false,
    },
  },
)

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {
  icon?: ReactNode
  interactive?: boolean
  /** Adds iOS-style press feedback for cards that act as a single control. */
  pressable?: boolean
}

const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    { className, variant, hover, interactive, pressable, children, ...props },
    ref,
  ) => (
    <div
      ref={ref}
      className={cn(
        cardVariants({ variant, hover, interactive, pressable }),
        className,
      )}
      {...props}
    >
      {children}
    </div>
  ),
)
Card.displayName = 'Card'

const CardHeader = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & { icon?: ReactNode }
>(({ className, children, icon, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5 p-6', className)}
    {...props}
  >
    {icon && (
      <div className="mb-3 w-fit rounded-lg bg-primary/5 p-2 transition-colors group-hover:bg-primary/10">
        {icon}
      </div>
    )}
    {children}
  </div>
))
CardHeader.displayName = 'CardHeader'

const CardTitle = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'text-xl font-semibold leading-tight tracking-tight',
        className,
      )}
      {...props}
    />
  ),
)
CardTitle.displayName = 'CardTitle'

const CardDescription = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
))
CardDescription.displayName = 'CardDescription'

const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
  ),
)
CardContent.displayName = 'CardContent'

const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center p-6 pt-0', className)}
      {...props}
    />
  ),
)
CardFooter.displayName = 'CardFooter'

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
